/**
 * The dialog in front of anything that cannot be undone.
 *
 * It states what will happen in plain words, names the thing by name, and puts
 * Cancel ahead of the destructive button in tab order, so the button the browser
 * focuses on opening is the harmless one. The busy state is honest: the button
 * says what it is doing and stays disabled, along with Escape, until the request
 * settles — a half-finished delete should not be dismissible.
 */

import type { ReactElement, ReactNode } from 'react';
import { Modal } from './Modal.js';

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  description: string;
  confirmLabel: string;
  busyLabel: string;
  busy: boolean;
  /** Shown above the buttons when the attempt already failed once. */
  error?: string | null;
  children?: ReactNode;
  onConfirm: () => void;
  onClose: () => void;
}

export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel,
  busyLabel,
  busy,
  error = null,
  children,
  onConfirm,
  onClose,
}: ConfirmDialogProps): ReactElement {
  return (
    <Modal
      open={open}
      title={title}
      description={description}
      onClose={busy ? () => undefined : onClose}
      footer={
        <>
          <button type="button" className="button button--ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="button button--danger" onClick={onConfirm} disabled={busy}>
            {busy ? busyLabel : confirmLabel}
          </button>
        </>
      }
    >
      {children}
      {error !== null ? (
        <p className="alert alert--danger" role="alert">
          {error}
        </p>
      ) : null}
    </Modal>
  );
}
